import React, { useState, useRef, useEffect } from "react";

import { styled, t } from "@superset-ui/core";
import { UploadOutlined, CloseCircleFilled } from "@ant-design/icons";
import { Input } from "src/components/Input";
import { InputNumber } from "antd";
import ControlHeader from "../../../ControlHeader";

/**
 * ImageUploadControl 패널로 전달되는 props 정의
 * @typedef {Object} ImageUploadControlProps
 * @property {string} label label 정보(aria-label 속성에 사용, html 요소의 설명용 텍스트로 화면상에 보여지는 label은 아님)
 * @property {string} placeholder input 에 보여질 placeholder
 * @property {Function} onChange 업데이트 된 정보들을 store에 저장하는 onchange 함수
 * @property {string} value 문자열로 변환된 파일 정보
 */
interface ImageUploadControlProps {
    label: string;
    placeholder?: string;
    onChange: (fileStr: string | undefined) => void;
    value?: string;
}

/**
 * 실제 업로드된 file의 데이터를 사용하기 위한 fileData 구조 정의 (store 저장 시, 문자열 변환)
 * @typedef {Object} FileUpload
 * @property {number} 파일 업로드 컴포넌트 식별을 위한 ID
 * @property {string | ArrayBuffer | null} file file 객체 정보
 * @property {string} fileName 파일 명
 * @property {number} min 범위 값 설정 중 최소 값
 * @property {number} max 범위 값 설정 중 최대 값
 */
interface FileUpload {
    id: number;
    file?: string | ArrayBuffer | null;
    fileName?: string;
    min?: number;
    max?: number;
}

const StyledImageUploadControlContainer = styled.div`
    position: relative;
    input[type="file"] {
        display: none;
    }
`;

const StyledUploadContainer = styled.div`
    ${({ theme }) => `
        position: relative;
        margin: 8px 0;
        padding: 8px;
        border-top: 1px solid ${theme.colors.grayscale.light2};
    `}
`;

// border-top: 1px solid #e0e0e0;
const StyledMinMaxInputRow = styled.div`
    display: flex;
    gap: 8px;
`;

const StyledFileInputDiv = styled.div`
    position: relative;
`;

const StyledButtonContainer = styled.div`
    ${({ theme }) => `
        display: flex;
        justify-content: flex-end;
        position: absolute;
        width: 24px;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        gap: 4px;
        color: ${theme.colors.icon.base}
    `}
`;

const StyledDeleteButton = styled(CloseCircleFilled)`
    ${({ theme }) => `
        cursor: pointer;
        &:hover {
            color: ${theme.colors.icon.hover}
        }
    `}
`;

const StyledUploadButton = styled(UploadOutlined)`
    ${({ theme }) => `
        cursor: pointer;
        &:hover {
            color: ${theme.colors.icon.hover}
        }
    `}
`;

const StyledInput = styled(Input)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-right: 40px;
`;

const StyledInputNumber = styled(InputNumber)`
    width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-right: 40px;
`;

const ImageUploadByCountControl = (props: ImageUploadControlProps) => {
    const { label, placeholder, onChange, value } = props;

    const fileInputRefs = useRef<HTMLInputElement[]>([]);
    const [uploads, setUploads] = useState<FileUpload[]>([]);
    const [uploadCount, setUploadCount] = useState<number>(1);

    const MAX_COUNT = 5;

    /**
     * @description [useEffect hooks] 기존 업데이트 된 value가 있는 경우 파일명 표시
     */
    useEffect(() => {
        if (value) {
            const parseJsonFileData = JSON.parse(value);
            setUploadCount(parseJsonFileData.length);
            setUploads(parseJsonFileData);
        }
    }, [value]);

    /**
     * @name handleUploadCountChange
     * @function
     * @description 업로드 컨트롤 생성 갯수 변경 이벤트
     * @return {void}
     */
    const handleUploadCountChange = (value: number): void => {
        if (value > 0 && value <= MAX_COUNT) {
            const updatedUploads = [...uploads];
            const numberOfObjectToCut = updatedUploads.length - value;
            if (numberOfObjectToCut > 0) {
                const cutUpdatedUploads = updatedUploads.slice(0, updatedUploads.length - numberOfObjectToCut);
                setUploads(cutUpdatedUploads);
                onChange(JSON.stringify(cutUpdatedUploads));
            }
            setUploadCount(value);
        }
    };

    /**
     * @name handleFileInputChange
     * @function
     * @description 파일 업로드 변경 이벤트
     * @return {void}
     */
    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const { files } = event.target;
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            if (file) {
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                    const updatedUploads = [...uploads];
                    if (updatedUploads[id]) {
                        updatedUploads[id].file = reader.result;
                        updatedUploads[id].fileName = file.name;
                    } else {
                        updatedUploads[id] = {
                            id,
                            file: reader.result,
                            fileName: file.name,
                        };
                    }
                    setUploads(updatedUploads);
                    onChange(JSON.stringify(updatedUploads));
                };
            }
        }
    };

    /**
     * @name handleMinInputChange
     * @function
     * @description 업로드 컨트롤 최소 값 입력 변경 이벤트
     * @return {void}
     */
    const handleMinInputChange = (uploadId: number, minValue: number): void => {
        const updatedUploads = [...uploads];
        if (updatedUploads[uploadId]) {
            updatedUploads[uploadId].min = minValue;
        } else {
            updatedUploads[uploadId] = { id: uploadId, min: minValue };
        }
        setUploads(updatedUploads);
        onChange(JSON.stringify(updatedUploads));
    };

    /**
     * @name handleMaxInputChange
     * @function
     * @description 업로드 컨트롤 최대 값 입력 변경 이벤트
     * @return {void}
     */
    const handleMaxInputChange = (uploadId: number, maxValue: number): void => {
        const updatedUploads = [...uploads];
        if (updatedUploads[uploadId]) {
            updatedUploads[uploadId].max = maxValue;
        } else {
            updatedUploads[uploadId] = { id: uploadId, max: maxValue };
        }
        setUploads(updatedUploads);
        onChange(JSON.stringify(updatedUploads));
    };

    /**
     * @name handleDeleteClick
     * @function
     * @description 삭제 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleDeleteClick = (uploadId: number): void => {
        fileInputRefs.current[uploadId].value = "";
        const updatedUploads = [...uploads];
        updatedUploads[uploadId].file = null;
        updatedUploads[uploadId].fileName = "";
        setUploads(updatedUploads);
        onChange(JSON.stringify(updatedUploads));
    };

    /**
     * @name handleUploadClick
     * @function
     * @description 업로드 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleUploadClick = (uploadId: number): void => {
        fileInputRefs.current[uploadId]?.click();
    };

    const dynamicRenderUploads = () =>
        Array.from({ length: uploadCount }, (_, index) => {
            const uploadId = index;
            return (
                <StyledUploadContainer key={uploadId}>
                    <StyledMinMaxInputRow>
                        <div>
                            <ControlHeader
                                {...{
                                    ...props,
                                    label: t("minimum"),
                                    description: t(
                                        "If the minimum value is an empty value, all values below the maximum value are applied."
                                    ),
                                }}
                            />
                            <StyledInputNumber
                                aria-label={label}
                                value={uploads[uploadId]?.min}
                                onChange={(value: number) => handleMinInputChange(uploadId, value)}
                            />
                        </div>
                        <div>
                            <ControlHeader
                                {...{
                                    ...props,
                                    label: t("maximum"),
                                    description: t(
                                        "If the maximum value is an empty value, all values above the minimum value are applied."
                                    ),
                                }}
                            />
                            <StyledInputNumber
                                aria-label={label}
                                value={uploads[uploadId]?.max}
                                onChange={(value: number) => handleMaxInputChange(uploadId, value)}
                            />
                        </div>
                    </StyledMinMaxInputRow>
                    <ControlHeader {...{ label: t("Set range values and upload images.") }} />
                    <StyledFileInputDiv>
                        <StyledInput
                            placeholder={placeholder}
                            value={uploads[uploadId]?.fileName}
                            aria-label={label}
                            readOnly
                        />
                        <StyledButtonContainer>
                            {uploads[uploadId]?.fileName && (
                                <StyledDeleteButton size={12} onClick={() => handleDeleteClick(uploadId)} />
                            )}
                            <StyledUploadButton size={12} onClick={() => handleUploadClick(uploadId)} />
                        </StyledButtonContainer>
                    </StyledFileInputDiv>
                    <input
                        type="file"
                        accept="image/*"
                        ref={(ref) => {
                            if (ref) {
                                fileInputRefs.current[uploadId] = ref;
                            }
                        }}
                        onChange={(e) => handleFileInputChange(e, uploadId)}
                    />
                </StyledUploadContainer>
            );
        });

    return (
        <StyledImageUploadControlContainer>
            <ControlHeader {...props} />
            <StyledInputNumber
                value={uploadCount}
                min={1}
                max={MAX_COUNT}
                aria-label={label}
                onChange={(value: number) => handleUploadCountChange(value)}
            />
            {uploadCount > 0 && dynamicRenderUploads()}
        </StyledImageUploadControlContainer>
    );
};

export default ImageUploadByCountControl;
